/**
 * Read-only views of per-method plan conditions as one shared list. The views exist only while every
 * method agrees on its rows; they return null when methods disagree, so callers never merge unrelated
 * rows.
 */
import {
  Condition,
  ConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {PlanConditionsByMethodId} from './planConditionState';

/**
 * True when every method has the same number of rows and the same name, description, and replicate
 * count at each index. Ids may still differ per method.
 */
export function arePlanConditionsUniform(planConditionsByMethodId: PlanConditionsByMethodId): boolean {
  const [firstRows, ...otherRows] = Object.values(planConditionsByMethodId);
  return (
    firstRows == null ||
    otherRows.every(
      (rows) =>
        rows.length === firstRows.length &&
        rows.every((row, index) => areConditionMetadataEqual(row, firstRows[index]))
    )
  );
}

/**
 * Condition groups for the flowchart and unit-operation cards when every method agrees.
 * Returns null when methods disagree. Each method's id is stored under that method in
 * `conditionIdsByPmdvId`. The shared table reads {@link getSharedConditionRows}.
 */
export function getSharedConditionGroups(
  planConditionsByMethodId: PlanConditionsByMethodId
): Array<ConditionGroup> | null {
  if (!arePlanConditionsUniform(planConditionsByMethodId)) {
    return null;
  }
  const methodRows = Object.entries(planConditionsByMethodId);
  const firstRows = methodRows[0]?.[1] ?? [];
  return firstRows.map((row, index) => {
    const ids = methodRows.map(([, rows]) => rows[index].id);
    const conditionIdsByPmdvId = ids.some((id) => id != null)
      ? Object.fromEntries(methodRows.map(([methodId], methodIndex) => [methodId, ids[methodIndex] ?? null]))
      : {};
    return conditionGroupFromRow(row, conditionIdsByPmdvId);
  });
}

/**
 * The flowchart condition group for one row, with the given condition ids keyed by method.
 */
export function conditionGroupFromRow(
  row: Condition,
  conditionIdsByPmdvId: ConditionGroup['conditionIdsByPmdvId']
): ConditionGroup {
  return {
    name: row.name,
    numReplicates: row.numReplicates,
    ...(row.description !== undefined ? {description: row.description} : {}),
    ...(row.validationState != null ? {validationState: row.validationState} : {}),
    conditionIdsByPmdvId,
  };
}

/**
 * Id-stripped rows for the shared table when every method agrees. Returns null when methods
 * disagree, so callers do not merge unrelated rows. A procedure swap reads this same list.
 */
export function getSharedConditionRows(
  planConditionsByMethodId: PlanConditionsByMethodId
): Array<Condition> | null {
  if (!arePlanConditionsUniform(planConditionsByMethodId)) {
    return null;
  }
  const [firstRows = []] = Object.values(planConditionsByMethodId);
  return firstRows.map((row) => ({...row, id: undefined}));
}

function areConditionMetadataEqual(left: Condition, right: Condition | undefined): boolean {
  return (
    right != null &&
    left.name === right.name &&
    (left.description ?? undefined) === (right.description ?? undefined) &&
    left.numReplicates === right.numReplicates
  );
}
